import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Card, Col, Input, Row, Space, Typography, message, Modal } from 'antd';
import { useAuth } from '../../components/common/security/AuthContext';
import { fetchSqlDataWithQuery, insertData } from '../../services/api';
import { updateData } from '../../services/db.api';
import { getTableNextSequenceValue } from '../../services/sequence.api';
import { useGetDestinationFolder } from '../../hooks/genericHooks';
import type { CorDestinationFolder } from '../../types';
import FileUploadTool, { FileUploadToolHandle } from '../../components/common/FileUploadTool';
import { queueNewTranslationLogRows } from '../../utils/dccTranslationQueue';
import Button from '../../components/common/Button';

const { Text } = Typography;

const normalizeStr = (v: any) => String(v ?? '').trim();

const sanitizePathSegment = (v: any): string => {
  return String(v ?? '')
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80);
};

const inferDocNoFromFilename = (fileName: string) => {
  const base = String(fileName || '').replace(/\\/g, '/').split('/').pop() || '';
  if (!base) return '';
  const lastDot = base.lastIndexOf('.');
  if (lastDot > 0) return base.slice(0, lastDot);
  return base;
};

const DccTranslationRequest: React.FC = () => {
  const { token } = useAuth();

  const [transmittalNo, setTransmittalNo] = useState('');
  const [draftKey, setDraftKey] = useState(() => {
    const g: any = globalThis as any;
    const uuid = g?.crypto?.randomUUID?.();
    if (uuid) return String(uuid);
    return `draft_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  });
  const [notes, setNotes] = useState('');

  const [generatingNo, setGeneratingNo] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  const primaryUploadRef = React.useRef<FileUploadToolHandle>(null);
  const supportingUploadRef = React.useRef<FileUploadToolHandle>(null);
  const retryingRef = React.useRef<boolean>(false);

  const routePath = '/dcc/translation/request';
  const { data: destinationFolders } = useGetDestinationFolder();

  const assignedRoot = useMemo(() => {
    const arr = (destinationFolders as any)?.data || destinationFolders;
    const rows: CorDestinationFolder[] = Array.isArray(arr) ? (arr as any) : [];
    const hit = rows.find((r: any) => normalizeStr((r as any)?.route_path) === routePath);
    return normalizeStr((hit as any)?.folder_path);
  }, [destinationFolders]);

  const requestBasePath = useMemo(() => {
    const tx = sanitizePathSegment(transmittalNo);
    if (!tx) return '';
    if (!assignedRoot) return '';
    return `${assignedRoot}/${tx}`;
  }, [assignedRoot, transmittalNo]);

  const tempSubpathPrimary = useMemo(() => {
    const dk = sanitizePathSegment(draftKey);
    if (!dk) return '';
    return `${dk}/primary`;
  }, [draftKey]);

  const tempSubpathSupporting = useMemo(() => {
    const dk = sanitizePathSegment(draftKey);
    if (!dk) return '';
    return `${dk}/supporting`;
  }, [draftKey]);

  const generateTransmittalNo = useCallback(async () => {
    try {
      setGeneratingNo(true);
      const nextId = await getTableNextSequenceValue('dcc_translation_requests', 'id');
      if (nextId == null) throw new Error('Failed to peek sequence');
      const padded = String(Number(nextId)).padStart(5, '0');
      setTransmittalNo(`TRREQ-${padded}`);
    } catch (e: any) {
      message.error(e?.response?.data?.error || e?.message || 'Failed to generate transmittal no');
    } finally {
      setGeneratingNo(false);
    }
  }, []);

  useEffect(() => {
    if (!transmittalNo) {
      generateTransmittalNo();
    }
  }, [generateTransmittalNo, transmittalNo]);

  const submitRequest = useCallback(async () => {
    if (!normalizeStr(notes)) {
      message.warning('Notes is required');
      return;
    }
    if (!token) {
      message.warning('Login required');
      return;
    }
    if (!assignedRoot) {
      message.warning('Upload root is not configured for this page. Please ask admin to assign a destination folder.');
      return;
    }

    const hasPrimary = !!primaryUploadRef.current?.hasFilesToUpload();
    const hasSupporting = !!supportingUploadRef.current?.hasFilesToUpload();
    if (!hasPrimary && !hasSupporting) {
      message.warning('Upload at least one file (Primary or Supporting)');
      return;
    }

    const currentUser = normalizeStr(localStorage.getItem('user') || '');
    let dbSaved = false;

    try {
      setSubmitting(true);
      let tx = normalizeStr(transmittalNo);
      if (!tx) {
        message.error('Transmittal number not available. Click Generate and try again.');
        return;
      }

      let finalBasePath = `${assignedRoot}/${sanitizePathSegment(tx)}`;
      let docRef = tx;
      const primaryNames = hasPrimary ? primaryUploadRef.current!.getUploadedFileNames() : [];
      const requestedBy = localStorage.getItem('user') || null;

      // 1) DB first (same pattern as other FileUploadTool pages) — files stay in temp until this succeeds
      const insResp: any = await insertData('dcc_translation_requests', {
        transmittal_no: tx,
        upload_path: finalBasePath,
        status: 'requested',
        source_folder_path: finalBasePath,
        requested_by: currentUser || null,
        notes: normalizeStr(notes),
        created_by: currentUser || null,
      });

      const insertedId = insResp?.id ?? insResp?.inserted_id ?? (Array.isArray(insResp?.ids) ? insResp.ids[0] : undefined);
      if (insertedId != null) {
        const paddedFromId = String(Number(insertedId)).padStart(5, '0');
        const idealTx = `TRREQ-${paddedFromId}`;
        if (idealTx !== tx) {
          const newBasePath = `${assignedRoot}/${sanitizePathSegment(idealTx)}`;
          await updateData('dcc_translation_requests', insertedId, {
            transmittal_no: idealTx,
            upload_path: newBasePath,
            source_folder_path: newBasePath,
          });
          tx = idealTx;
          finalBasePath = newBasePath;
          docRef = idealTx;
          setTransmittalNo(idealTx);
          Modal.info({
            title: 'Transmittal number adjusted',
            content: `The request number was updated from ${transmittalNo} to ${idealTx} to avoid a sequence race.`,
            okText: 'OK',
          });
        }
      }

      if (primaryNames.length) {
        await queueNewTranslationLogRows(
          primaryNames
            .map((fname) => normalizeStr(fname))
            .filter(Boolean)
            .map((fname) => {
              const src = `${finalBasePath}/${fname}`.replace(/\\/g, '/').replace(/^\/+/, '').replace(/\/+$/, '');
              return {
                transmittal_no: tx,
                document_no: inferDocNoFromFilename(fname),
                revision: '',
                folder_path: finalBasePath,
                source_file_path: src,
                status: 'queued',
                requested_by: requestedBy,
              };
            }),
        );
      }

      dbSaved = true;

      // 2) Then move temp → final destination
      const primaryUp = hasPrimary
        ? await primaryUploadRef.current!.finalizeUpload(docRef)
        : { success: true, folderPath: finalBasePath, movedFiles: [] as string[] };
      if (!primaryUp.success) {
        message.error(
          primaryUp.error ||
            'Request was saved, but primary file finalize failed. Files may still be in temp — retry finalize or contact support.'
        );
        return;
      }

      const supportingUp = hasSupporting
        ? await supportingUploadRef.current!.finalizeUpload(docRef)
        : { success: true, folderPath: finalBasePath, movedFiles: [] as string[] };
      if (!supportingUp.success) {
        message.error(
          supportingUp.error ||
            'Request was saved, but supporting file finalize failed. Some files may still be in temp — contact support.'
        );
        return;
      }

      message.success('Translation request submitted');
      setNotes('');
    } catch (e: any) {
      const errMsg = e?.response?.data?.error || e?.message || '';
      const isUniqueViolation = /23505|unique constraint|duplicate key/i.test(String(errMsg));
      if (isUniqueViolation) {
        message.info('Request number already taken, refreshing number…');
        try {
          await generateTransmittalNo();
          Modal.confirm({
            title: 'Number refreshed',
            content: 'The transmittal number was refreshed. Do you want to retry submit now?',
            okText: 'Retry submit',
            cancelText: 'Cancel',
            onOk: async () => {
              if (retryingRef.current) return;
              retryingRef.current = true;
              try {
                await submitRequest();
              } finally {
                retryingRef.current = false;
              }
            },
          });
        } catch (_) {
          // ignore secondary failure; general handler below will show error if needed
        }
      } else if (dbSaved) {
        message.error(
          errMsg || 'Request was saved, but file finalize failed. Please contact support.'
        );
      } else {
        message.error(errMsg || 'Failed to submit request');
      }
    } finally {
      setSubmitting(false);
    }
  }, [assignedRoot, notes, token, generateTransmittalNo]);

  return (
    <Card style={{ width: '100%', maxWidth: '100%' }}>
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        {!token && <Alert type="warning" message="Login required for uploads" showIcon />}
        {!!token && !assignedRoot && (
          <Alert
            type="warning"
            message="Upload root is not assigned for this page"
            description={`Ask admin to configure destination folder mapping for ${routePath} in /core/folders/assignments`}
            showIcon
          />
        )}

        <Row gutter={[8, 8]} align="middle">
          <Col xs={24} md={6}>
            <Input value={transmittalNo} readOnly placeholder="Transmittal no" />
          </Col>
          <Col xs={24} md={4}>
            <Button onClick={generateTransmittalNo} loading={generatingNo} disabled={generatingNo || submitting}>
              Generate
            </Button>
          </Col>
          <Col xs={24} md={18}>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" />
          </Col>
        </Row>

        <Text type="secondary">
          Upload root: <Text code>{assignedRoot || '-'}</Text>
          {'  '}Destination: <Text code>{requestBasePath || '-'}</Text>
        </Text>

        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <Text strong>Primary</Text>
            <FileUploadTool
              ref={primaryUploadRef}
              resetTrigger={draftKey}
              tempSubpath={tempSubpathPrimary}
              buttonText="Upload Files"
            />
          </Space>

          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <Text strong>Supporting</Text>
            <FileUploadTool
              ref={supportingUploadRef}
              resetTrigger={draftKey}
              tempSubpath={tempSubpathSupporting}
              buttonText="Upload Files"
            />
          </Space>
        </Space>

        <Space wrap>
          <Button
            action="add"
            type="primary"
            onClick={submitRequest}
            loading={submitting}
            disabled={!token || !assignedRoot || !normalizeStr(transmittalNo) || !normalizeStr(notes)}
          >
            Submit request
          </Button>
          <Button
            onClick={() => {
              primaryUploadRef.current?.reset({ deleteTemp: true });
              supportingUploadRef.current?.reset({ deleteTemp: true });
              setNotes('');
              const g: any = globalThis as any;
              const uuid = g?.crypto?.randomUUID?.();
              setDraftKey(uuid ? String(uuid) : `draft_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`);
            }}
            disabled={submitting}
          >
            Clear attachments
          </Button>
        </Space>
      </Space>
    </Card>
  );
};

export default DccTranslationRequest;
